import { create } from "zustand";

/**
 * The application's failure surface (backlog item 11).
 *
 * A zustand store rather than a React context on purpose: the `QueryClient` in
 * `main.tsx` is constructed at module scope, before anything renders, and its
 * `MutationCache.onError` is the one place every mutation failure in the app
 * already passes through. A context would need a provider that does not exist
 * yet at that moment; a store can simply be written to.
 *
 * `<Toaster />` is the only reader. It is mounted inside `AppShell`, which is
 * why the print routes — siblings of `AppShell` under the router root, not
 * children — cannot grow a toast layer by construction rather than by a CSS
 * rule somebody has to remember (PRD FR-9.1).
 */

export type ToastTone = "error" | "warning" | "info";

export interface Toast {
  id: number;
  tone: ToastTone;
  /** The short label: what failed. */
  title: string;
  /** The server's own sentence. Never a rewrite of it. */
  message: string;
  /** `ApiError.code`, kept for the test that asserts on it and for the DOM. */
  code?: string;
  /**
   * Announced as `assertive` and rendered with `role="alert"`.
   *
   * Reserved for the failures a reader must not miss mid-task — a destructive
   * action that did not happen, an expired session. An ordinary refusal is
   * `polite`, because interrupting a screen reader for every one of them is
   * its own kind of unusable.
   */
  urgent: boolean;
  /** How many identical failures this one row stands for. */
  count: number;
}

/** Beyond this the stack is noise; the oldest fall off the top. */
const MAX_VISIBLE = 4;

interface ToastState {
  toasts: Toast[];
  push: (toast: Omit<Toast, "id" | "count">) => void;
  dismiss: (id: number) => void;
  clear: () => void;
}

let nextId = 1;

export const useToasts = create<ToastState>()((set) => ({
  toasts: [],
  push: (toast) =>
    set((state) => {
      // A dashboard refresh that fails on eight tiles is one problem, not
      // eight. Collapsing on the rendered text rather than on the code keeps
      // two different connections failing for the same reason distinguishable.
      const previous = state.toasts.find(
        (entry) => entry.title === toast.title && entry.message === toast.message,
      );
      if (previous) {
        // Re-issued at the end so it restarts its dismissal timer and moves
        // back into view rather than expiring while it is still happening.
        const rest = state.toasts.filter((entry) => entry !== previous);
        return { toasts: [...rest, { ...previous, id: nextId++, count: previous.count + 1 }] };
      }
      const toasts = [...state.toasts, { ...toast, id: nextId++, count: 1 }];
      return { toasts: toasts.slice(-MAX_VISIBLE) };
    }),
  dismiss: (id) => set((state) => ({ toasts: state.toasts.filter((entry) => entry.id !== id) })),
  clear: () => set({ toasts: [] }),
}));

/** Raise a toast from outside React — the mutation cache's entry point. */
export function pushToast(toast: Omit<Toast, "id" | "count">): void {
  useToasts.getState().push(toast);
}
